import React, { FC, useMemo } from 'react';
import { Alert, Box, Card, LinearProgress, Stack, Typography } from '@mui/material';
import {
  AttachMoney as AttachMoneyIcon,
  Event as EventIcon,
  Hotel as HotelIcon,
  TrendingUp as TrendingUpIcon,
} from '@mui/icons-material';
import { formatNumber } from '../utils/formatters';
import { PERIOD_TYPES } from 'utils/constants/data';
import Dropdown from 'components/Dropdown';
import MetricCard from './MetricCard';

interface IndividualPerformanceProps {
  performanceData: any;
  loading: boolean;
  selectedPeriod: string | number;
  selectedPeriodType: string;
  periods: any[];
  onPeriodChange: (value: string | number) => void;
  onPeriodTypeChange: (value: string) => void;
  selectedManager?: string | number;
}

interface PerformanceMetricCardProps {
  title: string;
  actual: number;
  target: number;
  percentage: number;
  icon: React.ReactNode;
  formatValue: (value: number) => string;
  loading?: boolean;
}

const PerformanceMetricCard: FC<PerformanceMetricCardProps> = ({
  title,
  actual,
  target,
  percentage,
  icon,
  formatValue,
  loading,
}) => {
  const displayPercentage = Math.round(percentage);

  // Determine color based on achievement percentage
  const getColorScheme = (percentage: number) => {
    if (percentage < 40) {
      return {
        progressColor: '#EF4444', // Red
        textColor: '#EF4444',
        iconBgColor: '#FEE2E2', // Light red background
        iconColor: '#DC2626', // Darker red for icon
      };
    } else if (percentage >= 40 && percentage < 70) {
      return {
        progressColor: '#F97316', // Orange
        textColor: '#F97316',
        iconBgColor: '#FFEDD5', // Light orange background
        iconColor: '#EA580C', // Darker orange for icon
      };
    } else if (percentage >= 70 && percentage < 100) {
      return {
        progressColor: '#10B981', // Green
        textColor: '#10B981',
        iconBgColor: '#D1FAE5', // Light green background
        iconColor: '#059669', // Darker green for icon
      };
    } else {
      return {
        progressColor: '#047857', // Darker green
        textColor: '#047857',
        iconBgColor: '#A7F3D0', // Medium green background
        iconColor: '#065F46', // Dark green for icon
      };
    }
  };

  const colorScheme = getColorScheme(displayPercentage);

  return (
    <Card
      sx={{
        p: 2.5,
        borderRadius: 2,
        backgroundColor: '#fff',
        boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
        border: '1px solid',
        borderColor: 'divider',
        transition: 'all 0.2s',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        '&:hover': {
          boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
        },
      }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
        <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.8125rem', fontWeight: 500 }}>
          {title}
        </Typography>
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: '50%',
            backgroundColor: colorScheme.iconBgColor,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: colorScheme.iconColor,
          }}>
          {icon}
        </Box>
      </Box>

      {loading ? (
        <Box>
          <LinearProgress sx={{ mb: 1 }} />
          <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.secondary' }}>
            Loading...
          </Typography>
        </Box>
      ) : (
        <>
          <Box sx={{ mb: 1.5 }}>
            <Typography variant="h5" sx={{ fontWeight: 700, color: 'text.primary', mb: 0.5 }}>
              {formatValue(actual)}
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
              Target: {formatValue(target)}
            </Typography>
          </Box>

          <Box sx={{ mt: 'auto' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
              <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem', fontWeight: 500 }}>
                Achievement
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  color: colorScheme.textColor,
                  fontSize: '0.75rem',
                  fontWeight: 600,
                }}>
                {displayPercentage}%
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={Math.min(displayPercentage, 100)}
              sx={{
                height: 8,
                borderRadius: 4,
                backgroundColor: 'grey.200',
                '& .MuiLinearProgress-bar': {
                  backgroundColor: colorScheme.progressColor,
                },
              }}
            />
          </Box>
        </>
      )}
    </Card>
  );
};

const IndividualPerformance: FC<IndividualPerformanceProps> = ({
  performanceData,
  loading,
  selectedPeriod,
  selectedPeriodType,
  periods,
  onPeriodChange,
  onPeriodTypeChange,
  selectedManager,
}) => {
  const metrics = useMemo(() => {
    if (!performanceData) return [];

    const roomNight = performanceData?.roomNight || { target: 0, achieved: 0, variance: 0, percentage: 0 };
    const roomRevenue = performanceData?.roomRevenue || { target: 0, achieved: 0, variance: 0, percentage: 0 };
    const miceCount = performanceData?.miceCount || { target: 0, achieved: 0, variance: 0, percentage: 0 };
    const miceRevenue = performanceData?.miceRevenue || { target: 0, achieved: 0, variance: 0, percentage: 0 };

    return [
      {
        title: 'Room Nights',
        actual: Number(roomNight.achieved ?? 0),
        target: Number(roomNight.target ?? 0),
        percentage: Number(roomNight.percentage ?? 0),
        variance: Number(roomNight.variance ?? 0),
        icon: <HotelIcon />,
        accentClassName: 'bg-sky-100 text-sky-700',
        formatValue: (val: number) => formatNumber(val),
      },
      {
        title: 'Room Revenue',
        actual: Number(roomRevenue.achieved ?? 0),
        target: Number(roomRevenue.target ?? 0),
        percentage: Number(roomRevenue.percentage ?? 0),
        variance: Number(roomRevenue.variance ?? 0),
        icon: <AttachMoneyIcon />,
        accentClassName: 'bg-emerald-100 text-emerald-700',
        formatValue: (val: number) => `${formatNumber(val)}`,
      },
      {
        title: 'Event Count',
        actual: Number(miceCount.achieved ?? 0),
        target: Number(miceCount.target ?? 0),
        percentage: Number(miceCount.percentage ?? 0),
        variance: Number(miceCount.variance ?? 0),
        icon: <EventIcon />,
        accentClassName: 'bg-amber-100 text-amber-700',
        formatValue: (val: number) => formatNumber(val),
      },
      {
        title: 'Event Revenue',
        actual: Number(miceRevenue.achieved ?? 0),
        target: Number(miceRevenue.target ?? 0),
        percentage: Number(miceRevenue.percentage ?? 0),
        variance: Number(miceRevenue.variance ?? 0),
        icon: <TrendingUpIcon />,
        accentClassName: 'bg-violet-100 text-violet-700',
        formatValue: (val: number) => `${formatNumber(val)}`,
      },
    ];
  }, [performanceData]);

  return (
    <Card sx={{ p: 3, borderRadius: 2, backgroundColor: '#fff', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          justifyContent: 'space-between',
          alignItems: { xs: 'flex-start', md: 'center' },
          mb: 2,
          gap: 2,
        }}>
        <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary' }}>
          Individual Performance
        </Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <Dropdown
            label="Period Type"
            value={selectedPeriodType}
            options={PERIOD_TYPES}
            onChange={value => onPeriodTypeChange(value as string)}
            minWidth={150}
          />
          <Dropdown
            label="Period"
            value={selectedPeriod}
            options={periods.map((p: any) => ({
              id: p.value,
              name: p.label || p.name,
            }))}
            onChange={value => onPeriodChange(value)}
            minWidth={220}
          />
        </Stack>
      </Box>
      {!selectedPeriod || !selectedPeriodType ? (
        <Alert severity="info" sx={{ mt: 2 }}>
          Please select a period to view individual performance metrics.
        </Alert>
      ) : selectedPeriod && selectedPeriodType && !selectedManager ? (
        <Alert severity="info" sx={{ mt: 2 }}>
          Please select sales executive.
        </Alert>
      ) : loading && !performanceData ? (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
          {[1, 2, 3, 4].map(i => (
            <Box
              key={i}
              sx={{
                flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 8px)', md: '1 1 calc(25% - 12px)' },
                minWidth: { xs: '100%', sm: 'calc(50% - 8px)', md: 'calc(25% - 12px)' },
              }}>
              <MetricCard
                title="Loading..."
                value=""
                icon={<HotelIcon />}
                loading={true}
                accentClassName="bg-gray-100 text-gray-700"
              />
            </Box>
          ))}
        </Box>
      ) : performanceData ? (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
          {metrics.map((metric, index) => (
            <Box
              key={metric.title}
              sx={{
                flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 8px)', md: '1 1 calc(25% - 12px)' },
                minWidth: { xs: '100%', sm: 'calc(50% - 8px)', md: 'calc(25% - 12px)' },
              }}>
              <PerformanceMetricCard {...metric} loading={loading && index === 0} />
            </Box>
          ))}
        </Box>
      ) : null}
    </Card>
  );
};

export default IndividualPerformance;
